import Link from "next/link";
import Image from "next/image";
import { listPosts } from "@/lib/cms";
import { PreviewBar } from "../_bar";

export const metadata = { title: "Mockup B · Cinema Magazine · Preview" };
export const dynamic = "force-dynamic";

const CAT_IDS = { tinNong: 148, phanTich: 36, crypto: 37, vang: 252, chungKhoan: 40 };

/**
 * MOCKUP B — Cinema Magazine.
 * WIRED feature / Bloomberg Originals / Polygon longform.
 * Black background, full-bleed photographic hero, masthead overlaid on image,
 * mosaic asymmetric secondary, big white-on-black numerals as section markers.
 */
export default async function MockupBPage() {
  const [
    { posts: feat },
    { posts: latest },
    { posts: phanTich },
    { posts: tinNong },
    { posts: chungKhoan },
  ] = await Promise.all([
    listPosts({ perPage: 1, featured: true, orderBy: "date", order: "desc" }),
    listPosts({ perPage: 8, orderBy: "date", order: "desc" }),
    listPosts({ perPage: 4, categories: [CAT_IDS.phanTich] }),
    listPosts({ perPage: 6, categories: [CAT_IDS.tinNong] }),
    listPosts({ perPage: 4, categories: [CAT_IDS.chungKhoan] }),
  ]);

  const heroPost = feat[0] ?? latest[0];
  const used = new Set<string | number>();
  if (heroPost) used.add(heroPost.id);
  const mosaic1 = phanTich[0];
  const mosaic2 = phanTich[1];
  const mosaic3 = phanTich[2];
  if (mosaic1) used.add(mosaic1.id);
  if (mosaic2) used.add(mosaic2.id);
  if (mosaic3) used.add(mosaic3.id);
  const tinFour = tinNong.filter((p) => !used.has(p.id)).slice(0, 4);
  tinFour.forEach((p) => used.add(p.id));
  const ckThree = chungKhoan.filter((p) => !used.has(p.id)).slice(0, 3);

  return (
    <div style={{ background: "#0B0B0C", color: "#F4EFE3", minHeight: "100vh" }}>
      <PreviewBar active="b" />

      {/* HERO — full-bleed photo with overlaid masthead */}
      {heroPost && (
        <section className="relative">
          <div className="relative h-[92vh] min-h-[700px] w-full overflow-hidden">
            {heroPost.featured ? (
              <Image
                src={heroPost.featured.url}
                alt={heroPost.featured.alt}
                fill
                priority
                sizes="100vw"
                className="object-cover"
                style={{ filter: "saturate(1.05) contrast(1.05)" }}
              />
            ) : (
              <div
                className="absolute inset-0"
                style={{
                  background:
                    "linear-gradient(135deg,#1a1410 0%,#3d2818 50%,#0B0B0C 100%)",
                }}
              />
            )}
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(180deg, rgba(11,11,12,0.7) 0%, rgba(11,11,12,0.15) 35%, rgba(11,11,12,0.6) 75%, rgba(11,11,12,0.95) 100%)",
              }}
            />

            {/* Masthead on image */}
            <header className="absolute top-0 inset-x-0 z-10">
              <div className="mx-auto max-w-[1400px] px-8 lg:px-14 pt-8 lg:pt-10 flex items-center justify-between gap-6">
                <Link
                  href="/"
                  style={{
                    fontFamily: "var(--font-fraunces)",
                    fontSize: 32,
                    lineHeight: 1,
                    fontWeight: 500,
                    letterSpacing: "-0.02em",
                    color: "#F4EFE3",
                  }}
                >
                  Finzone
                </Link>
                <nav className="hidden lg:flex items-center gap-8 text-[12px] uppercase tracking-[0.22em] font-medium text-[#F4EFE3]/80">
                  {[
                    ["Tin nóng","/category/tin-nong"],
                    ["Phân tích","/category/phan-tich"],
                    ["Crypto","/category/crypto"],
                    ["Chứng khoán","/category/chung-khoan"],
                    ["Vàng","/category/vang"],
                  ].map(([l,h]) => (
                    <Link key={h} href={h!} className="hover:text-white">{l}</Link>
                  ))}
                </nav>
                <Link
                  href="/tim-kiem"
                  className="text-[12px] uppercase tracking-[0.22em] border border-white/30 px-4 py-2 rounded-full hover:border-white text-white/80 hover:text-white"
                >
                  Tìm kiếm
                </Link>
              </div>
            </header>

            {/* Title bottom-left */}
            <div className="absolute inset-x-0 bottom-0 z-10 pb-14 lg:pb-20">
              <div className="mx-auto max-w-[1400px] px-8 lg:px-14">
                <div className="max-w-5xl">
                  <div className="flex items-center gap-3 mb-6">
                    <span
                      className="px-3 py-1 text-[10px] uppercase tracking-[0.3em] font-bold text-black"
                      style={{ background: "#E8B86A" }}
                    >
                      {heroPost.categories[0]?.name ?? "Trang nhất"}
                    </span>
                    <span className="text-[11px] uppercase tracking-[0.25em] font-mono text-white/70">
                      {new Date(heroPost.date).toLocaleDateString("vi-VN")} · {heroPost.readMinutes} phút đọc
                    </span>
                  </div>
                  <Link href={`/${heroPost.slug}`} className="block group">
                    <h1
                      style={{
                        fontFamily: "var(--font-fraunces)",
                        fontSize: "clamp(46px, 7.4vw, 124px)",
                        lineHeight: 0.96,
                        letterSpacing: "-0.025em",
                        fontWeight: 500,
                        color: "#F4EFE3",
                        fontVariationSettings: "'opsz' 144, 'SOFT' 30, 'WONK' 1",
                      }}
                      className="group-hover:text-[#E8B86A] transition-colors"
                    >
                      {heroPost.title}
                    </h1>
                  </Link>
                  {heroPost.excerpt && (
                    <p
                      className="mt-7 max-w-3xl text-white/85 text-[18px] lg:text-[22px] leading-[1.5] italic"
                      style={{ fontFamily: "var(--font-newsreader)" }}
                    >
                      {heroPost.excerpt}
                    </p>
                  )}
                  <div className="mt-8 flex items-center gap-3 text-[12px] uppercase tracking-[0.22em] font-mono text-white/70">
                    <span className="text-white/95 font-semibold">{heroPost.author.name}</span>
                    <span>·</span>
                    <Link
                      href={`/${heroPost.slug}`}
                      className="border-b border-white/40 pb-0.5 hover:border-white text-white"
                    >
                      Đọc bài đầy đủ →
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Scroll cue */}
            <div className="absolute right-8 lg:right-14 top-1/2 -translate-y-1/2 hidden lg:flex flex-col items-center gap-3 text-white/60">
              <span className="text-[10px] uppercase tracking-[0.3em] font-mono [writing-mode:vertical-rl]">
                Scroll
              </span>
              <span className="w-px h-16 bg-white/30" />
            </div>
          </div>
        </section>
      )}

      <main>
        {/* MOSAIC — section II */}
        {mosaic1 && (
          <section style={{ background: "#0B0B0C", borderTop: "1px solid #1f1c17" }}>
            <div className="mx-auto max-w-[1400px] px-8 lg:px-14 py-20 lg:py-28">
              <div className="grid grid-cols-12 gap-x-6 mb-14 items-baseline">
                <span className="col-span-12 lg:col-span-2 text-[100px] lg:text-[140px] leading-none font-light text-[#3a3530]"
                  style={{ fontFamily: "var(--font-fraunces)", fontVariationSettings: "'opsz' 144" }}>
                  II
                </span>
                <div className="col-span-12 lg:col-span-10">
                  <div className="text-[10px] uppercase tracking-[0.35em] font-mono text-[#E8B86A] mb-4">
                    Phân tích chuyên sâu
                  </div>
                  <h2
                    style={{
                      fontFamily: "var(--font-fraunces)",
                      fontSize: "clamp(36px, 4.8vw, 68px)",
                      lineHeight: 1.05,
                      fontWeight: 400,
                      color: "#F4EFE3",
                      letterSpacing: "-0.02em",
                      fontStyle: "italic",
                      fontVariationSettings: "'opsz' 144, 'SOFT' 50",
                    }}
                  >
                    Đằng sau những con số.
                  </h2>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
                <Link href={`/${mosaic1.slug}`} className="lg:col-span-8 group block">
                  <div className="aspect-[16/10] relative overflow-hidden bg-[#1a1814] mb-6">
                    {mosaic1.featured ? (
                      <Image
                        src={mosaic1.featured.url}
                        alt={mosaic1.featured.alt}
                        fill
                        sizes="(max-width: 1024px) 100vw, 66vw"
                        className="object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                      />
                    ) : (
                      <div className="absolute inset-0 bg-gradient-to-br from-[#3a2818] to-[#0B0B0C]" />
                    )}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-[#E8B86A] mb-3">
                    {mosaic1.categories[0]?.name ?? "Phân tích"}
                  </div>
                  <h3
                    className="group-hover:text-[#E8B86A] transition-colors"
                    style={{
                      fontFamily: "var(--font-fraunces)",
                      fontSize: "clamp(28px, 3.4vw, 42px)",
                      lineHeight: 1.1,
                      fontWeight: 500,
                      color: "#F4EFE3",
                      fontVariationSettings: "'opsz' 144",
                    }}
                  >
                    {mosaic1.title}
                  </h3>
                  {mosaic1.excerpt && (
                    <p className="mt-4 text-[16px] leading-[1.6] text-[#C9BFA8] italic max-w-2xl"
                      style={{ fontFamily: "var(--font-newsreader)" }}>
                      {mosaic1.excerpt}
                    </p>
                  )}
                </Link>
                <div className="lg:col-span-4 space-y-10 lg:border-l lg:pl-10" style={{ borderColor: "#1f1c17" }}>
                  {[mosaic2, mosaic3].filter(Boolean).map((p) => (
                    <Link key={p!.id} href={`/${p!.slug}`} className="group block">
                      {p!.featured && (
                        <div className="aspect-[3/2] relative overflow-hidden bg-[#1a1814] mb-4">
                          <Image
                            src={p!.featured.url}
                            alt={p!.featured.alt}
                            fill
                            sizes="(max-width: 1024px) 100vw, 33vw"
                            className="object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                          />
                        </div>
                      )}
                      <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-[#E8B86A] mb-2">
                        {p!.categories[0]?.name ?? "Phân tích"}
                      </div>
                      <h3
                        className="group-hover:text-[#E8B86A] transition-colors"
                        style={{
                          fontFamily: "var(--font-fraunces)",
                          fontSize: 22,
                          lineHeight: 1.2,
                          fontWeight: 500,
                          color: "#F4EFE3",
                        }}
                      >
                        {p!.title}
                      </h3>
                      <div className="mt-2 text-[11px] uppercase tracking-[0.2em] font-mono text-[#9C8B6F]">
                        {p!.readMinutes} phút đọc
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* TIN NÓNG — 4 polaroid cards on cream interlude */}
        {tinFour.length >= 3 && (
          <section style={{ background: "#F4EFE3", color: "#1A1410" }}>
            <div className="mx-auto max-w-[1400px] px-8 lg:px-14 py-20 lg:py-28">
              <div className="grid grid-cols-12 gap-x-6 mb-14 items-baseline">
                <span className="col-span-12 lg:col-span-2 text-[100px] lg:text-[140px] leading-none font-light text-[#D8CDB5]"
                  style={{ fontFamily: "var(--font-fraunces)", fontVariationSettings: "'opsz' 144" }}>
                  III
                </span>
                <div className="col-span-12 lg:col-span-10">
                  <div className="text-[10px] uppercase tracking-[0.35em] font-mono text-[#9C6B2A] mb-4">
                    Tin nóng đang chạy
                  </div>
                  <h2
                    style={{
                      fontFamily: "var(--font-fraunces)",
                      fontSize: "clamp(36px, 4.8vw, 64px)",
                      lineHeight: 1.05,
                      fontWeight: 400,
                      letterSpacing: "-0.02em",
                      fontVariationSettings: "'opsz' 144, 'SOFT' 30, 'WONK' 1",
                    }}
                  >
                    Thị trường, tức thời.
                  </h2>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
                {tinFour.map((p) => (
                  <Link key={p.id} href={`/${p.slug}`} className="group block">
                    <div className="aspect-[4/5] relative overflow-hidden bg-[#E5DCC7] mb-5">
                      {p.featured ? (
                        <Image
                          src={p.featured.url}
                          alt={p.featured.alt}
                          fill
                          sizes="(max-width: 768px) 100vw, 25vw"
                          className="object-cover transition-transform duration-700 group-hover:scale-[1.05]"
                        />
                      ) : (
                        <div
                          className="absolute inset-0"
                          style={{
                            background: "linear-gradient(135deg,#9C6B2A,#E8B86A)",
                          }}
                        />
                      )}
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-[#9C6B2A] mb-2">
                      {p.categories[0]?.name ?? "Tin nóng"}
                    </div>
                    <h3
                      className="group-hover:text-[#9C6B2A] transition-colors"
                      style={{
                        fontFamily: "var(--font-fraunces)",
                        fontSize: 21,
                        lineHeight: 1.18,
                        fontWeight: 500,
                      }}
                    >
                      {p.title}
                    </h3>
                    <div className="mt-3 text-[11px] uppercase tracking-[0.2em] font-mono text-[#7A6B55]">
                      {p.readMinutes} phút
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CHỨNG KHOÁN — pull-quote then 3-col text */}
        {ckThree.length >= 1 && (
          <section style={{ background: "#0B0B0C", borderTop: "1px solid #1f1c17" }}>
            <div className="mx-auto max-w-[1100px] px-8 lg:px-14 py-24 lg:py-32 text-center">
              <span className="text-[10px] uppercase tracking-[0.35em] font-mono text-[#E8B86A]">
                Chứng khoán
              </span>
              {ckThree[0]?.excerpt && (
                <blockquote
                  className="mt-8 mb-12 italic"
                  style={{
                    fontFamily: "var(--font-newsreader)",
                    fontSize: "clamp(28px, 4.4vw, 56px)",
                    lineHeight: 1.15,
                    fontWeight: 400,
                    color: "#F4EFE3",
                  }}
                >
                  <span className="text-[#E8B86A] mr-1">“</span>
                  {ckThree[0].excerpt.slice(0, 220).replace(/\s+\S*$/, "")}…
                  <span className="text-[#E8B86A] ml-1">”</span>
                </blockquote>
              )}
              <Link
                href={`/${ckThree[0].slug}`}
                className="inline-block text-[12px] uppercase tracking-[0.25em] font-mono border-b border-[#E8B86A] pb-1 text-[#E8B86A] hover:text-white hover:border-white"
              >
                Đọc bài → {ckThree[0].title}
              </Link>
            </div>

            {ckThree.length >= 2 && (
              <div className="mx-auto max-w-[1400px] px-8 lg:px-14 pb-24">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-x-10 gap-y-10 border-t pt-10" style={{ borderColor: "#1f1c17" }}>
                  {ckThree.slice(1).map((p, i) => (
                    <article key={p.id} className={i > 0 ? "md:border-l md:pl-10" : ""} style={{ borderColor: "#1f1c17" }}>
                      <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-[#E8B86A] mb-3">
                        {p.categories[0]?.name ?? "Chứng khoán"}
                      </div>
                      <Link href={`/${p.slug}`} className="block group">
                        <h3
                          className="group-hover:text-[#E8B86A] transition-colors mb-3"
                          style={{
                            fontFamily: "var(--font-fraunces)",
                            fontSize: 24,
                            lineHeight: 1.18,
                            fontWeight: 500,
                            color: "#F4EFE3",
                          }}
                        >
                          {p.title}
                        </h3>
                      </Link>
                      {p.excerpt && (
                        <p className="text-[14px] leading-[1.6] text-[#C9BFA8] line-clamp-3"
                          style={{ fontFamily: "var(--font-newsreader)" }}>
                          {p.excerpt}
                        </p>
                      )}
                    </article>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}

        {/* Footer */}
        <footer style={{ background: "#0B0B0C", borderTop: "1px solid #1f1c17", color: "#F4EFE3" }}>
          <div className="mx-auto max-w-[1400px] px-8 lg:px-14 py-20 lg:py-28">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
              <div className="lg:col-span-7">
                <Link href="/" style={{
                  fontFamily: "var(--font-fraunces)",
                  fontSize: "clamp(64px, 10vw, 160px)",
                  lineHeight: 0.9,
                  letterSpacing: "-0.04em",
                  fontWeight: 400,
                  color: "#F4EFE3",
                  fontVariationSettings: "'opsz' 144, 'SOFT' 30, 'WONK' 1",
                }} className="inline-block">
                  Finzone
                </Link>
                <p className="mt-5 italic text-[18px] text-[#C9BFA8] max-w-xl"
                  style={{ fontFamily: "var(--font-newsreader)" }}>
                  Tin tài chính · Phân tích thị trường · Cho người đầu tư có suy nghĩ.
                </p>
              </div>
              <div className="lg:col-span-5 grid grid-cols-2 gap-6 text-[13px]">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-[#9C8B6F] mb-3">Chuyên mục</div>
                  <ul className="space-y-2">
                    {[["Tin nóng","/category/tin-nong"],["Phân tích","/category/phan-tich"],["Crypto","/category/crypto"],["Chứng khoán","/category/chung-khoan"]].map(([l,h]) => (
                      <li key={h}><Link href={h!} className="text-[#C9BFA8] hover:text-[#E8B86A]">{l}</Link></li>
                    ))}
                  </ul>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-[#9C8B6F] mb-3">Tòa soạn</div>
                  <ul className="space-y-2">
                    {[["Giới thiệu","/gioi-thieu"],["Liên hệ","/lien-he"],["Cộng đồng","/cong-dong"],["Bảo mật","/bao-mat"]].map(([l,h]) => (
                      <li key={h}><Link href={h!} className="text-[#C9BFA8] hover:text-[#E8B86A]">{l}</Link></li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
            <div className="mt-16 pt-6 border-t flex justify-between items-center text-[10px] uppercase tracking-[0.25em] font-mono text-[#7A6B55]"
              style={{ borderColor: "#1f1c17" }}>
              <span>© {new Date().getFullYear()} Finzone Network</span>
              <span className="italic" style={{ fontFamily: "var(--font-newsreader)" }}>Made in Vietnam</span>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
}
